import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { TENANTS, TENANT_LIST, DEFAULT_TENANT_ID, getTenant } from '../data/tenants';
import { getBranding, CLEAREQUAL_BRANDING } from '../data/tenantBranding';
import { resolveTenant, getEnvironment } from '../utils/tenantResolver';
import { subscribe as subscribeTenantConfig } from '../services/tenantConfigStore';

const TenantContext = createContext(null);

const STORAGE_KEY = 'clearequal.activeTenant';

/**
 * Convert a hex colour to a CSS gradient that matches the look of the
 * built-in --color-primary (which is a 135deg gradient between two teals).
 * We approximate by using the colour itself plus a 12%-lighter variant.
 */
const buildGradient = (hex) => {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return hex;
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, ((n >> 16) & 0xff) + 30);
  const g = Math.min(255, ((n >>  8) & 0xff) + 30);
  const b = Math.min(255, ( n        & 0xff) + 30);
  const lighter = '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
  return `linear-gradient(135deg, ${hex} 0%, ${lighter} 100%)`;
};

/**
 * Apply tenant branding to the document.
 * IMPORTANT: We override BOTH the new --tenant-* vars (for components
 * that opt in) AND the legacy --color-primary / --color-primary-solid
 * vars used by Tailwind's bg-primary / text-primary classes. Otherwise
 * existing buttons keep their teal default no matter what the admin sets.
 */
const applyBranding = (branding) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const c = branding.colors || CLEAREQUAL_BRANDING.colors;

  // New tenant-* vars (used by future components)
  root.style.setProperty('--tenant-primary', c.primary);
  root.style.setProperty('--tenant-primary-hover', c.primaryHover);
  root.style.setProperty('--tenant-on-primary', c.onPrimary);
  root.style.setProperty('--tenant-accent', c.accent);

  // Legacy vars (used by Tailwind classes throughout the app, bg-primary,
  // bg-accent, text-primary-solid, border-primary-solid, etc.). Overriding
  // these is what actually makes buttons and links pick up the customer brand.
  root.style.setProperty('--color-primary', buildGradient(c.primary));
  root.style.setProperty('--color-primary-solid', c.primary);
  root.style.setProperty('--color-primary-foreground', c.onPrimary);
  root.style.setProperty('--color-accent', c.accent);
  root.style.setProperty('--color-accent-foreground', c.onPrimary);

  if (branding.displayName && branding.displayName !== 'ClearEqual') {
    document.title = `${branding.displayName} | Pay Equity`;
  } else {
    document.title = 'ClearEqual | Pay Equity Compliance';
  }
};

export const namespacedKey = (key, tenantId) => {
  if (!tenantId) return key;
  return `tenant.${tenantId}.${key}`;
};

export const TenantProvider = ({ children }) => {
  const initial = useMemo(() => resolveTenant(), []);
  const [tenantId, setTenantId] = useState(initial.tenantId);
  const [tenantSource] = useState(initial.source);
  const [tenantLocked] = useState(initial.locked);
  const [environment] = useState(() => getEnvironment());

  // Bump on every save in tenantConfigStore so branding is re-read from
  // localStorage. This is the bit that was missing, admins were saving
  // new logos and colours but the Header memoised the old ones forever.
  const [configVersion, setConfigVersion] = useState(0);

  const [isTransitioning, setIsTransitioning] = useState(false);

  const tenant = getTenant(tenantId);
  // When the tenant comes from the default fallback (no subdomain, no query
  // param, no stored choice), apply ClearEqual's own brand colors even
  // though the data tenant is set. Otherwise the bare app.clearequal.com URL
  // would inherit the default tenant's brand (e.g. Noravia magenta).
  const branding = useMemo(() => {
    const dataBranding = getBranding(tenantId);
    if (tenantSource === 'default') {
      return { ...dataBranding, colors: CLEAREQUAL_BRANDING.colors, displayName: 'ClearEqual', logoUrl: null };
    }
    return dataBranding;
  }, [tenantId, configVersion, tenantSource]);

  // Re-read branding whenever the admin saves new config
  useEffect(() => {
    return subscribeTenantConfig(() => setConfigVersion((v) => v + 1));
  }, []);

  // Also listen to direct localStorage edits (cross-tab) and storage events
  // from non-store writers (defensive)
  useEffect(() => {
    const handler = (e) => {
      if (!e || !e.key || e.key.startsWith('clearequal.tenantConfig.')) {
        setConfigVersion((v) => v + 1);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  useEffect(() => {
    applyBranding(branding);
  }, [branding]);

  const switchTenant = useCallback((newTenantId) => {
    if (!TENANTS[newTenantId] || newTenantId === tenantId) return;
    if (tenantLocked) {
      console.warn('[TenantContext] Switch blocked, tenant is locked by subdomain');
      return;
    }
    setIsTransitioning(true);
    setTimeout(() => {
      setTenantId(newTenantId);
      try { localStorage.setItem(STORAGE_KEY, newTenantId); } catch (_) {}
      setTimeout(() => setIsTransitioning(false), 250);
    }, 180);
  }, [tenantId, tenantLocked]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, tenantId); } catch (_) {}
  }, [tenantId]);

  const value = {
    tenant,
    tenantId,
    tenants: TENANT_LIST,
    branding,
    switchTenant,
    isTransitioning,
    tenantSource,
    tenantLocked,
    environment,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return ctx;
};
